import { Resource } from './resource';
import { Filter } from './filters';
import { ReannotateDataReason } from './reannotate-data-capture';
import { Sort, SortDirection, SortColumn } from '../types/sort';
import { Capture, Sensor, Note, NoteAction } from './api';

export enum SensorTabs {
  CAPTURES = 'CAPTURES',
  NOTES = 'NOTES',
  DIAGNOSTIC_INFO = 'DIAGNOSTIC_INFO',
}

export type SensorsState = {
  resource: Resource<{
    sensor: Sensor;
  }>;

  activeTab: SensorTabs;

  captures: {
    filters: Array<Filter>;
    searchText: string;
    sort: Sort;
    selectedCaptureIds: Array<Capture['id']>;
    resource: Resource<Array<Capture>>;
  };

  notes: {
    resource: Resource<Array<Note>>;
    editing:
      | {
          active: true;
          id: Note['id'];
          body: string;
          submitDisabled: boolean;
        }
      | { active: false };
    creation: {
      active: boolean;
      body: string;
      submitDisabled: boolean;
    };
    currentlyDeletingNoteId: Note['id'] | null;
  };
  reannotateReason: ReannotateDataReason;
};

export enum SensorsActionTypes {
  SENSORS_MOUNT_DETAIL = 'SENSORS_MOUNT_DETAIL',
  SENSORS_UNMOUNT_DETAIL = 'SENSORS_UNMOUNT_DETAIL',
  SENSORS_RELOAD_DETAIL = 'SENSORS_RELOAD_DETAIL',

  SENSORS_LOAD_START = 'SENSORS_LOAD_START',
  SENSORS_LOAD_ERROR = 'SENSORS_LOAD_ERROR',
  SENSORS_LOAD_COMPLETE = 'SENSORS_LOAD_COMPLETE',

  SENSORS_SWITCH_TAB = 'SENSORS_SWITCH_TAB',

  SENSORS_CAPTURES_LOAD_START = 'SENSORS_CAPTURES_LOAD_START',
  SENSORS_CAPTURES_LOAD_ERROR = 'SENSORS_CAPTURES_LOAD_ERROR',
  SENSORS_CAPTURES_LOAD_COMPLETE = 'SENSORS_CAPTURES_LOAD_COMPLETE',
  SENSORS_CAPTURES_SET_FILTERS = 'SENSORS_CAPTURES_SET_FILTERS',
  SENSORS_CAPTURES_SET_SEARCH = 'SENSORS_CAPTURES_SET_SEARCH',
  SENSORS_CAPTURES_SET_SORT = 'SENSORS_CAPTURES_SET_SORT',
  SENSORS_CAPTURES_CHANGE_CHECKS = 'SENSORS_CAPTURES_CHANGE_CHECKS',

  SENSORS_NOTES_LOAD_START = 'SENSORS_NOTES_LOAD_START',
  SENSORS_NOTES_LOAD_ERROR = 'SENSORS_NOTES_LOAD_ERROR',
  SENSORS_NOTES_LOAD_COMPLETE = 'SENSORS_NOTES_LOAD_COMPLETE',

  SENSORS_NOTES_CREATION_OPEN = 'SENSORS_NOTES_CREATION_OPEN',
  SENSORS_NOTES_CREATION_CLOSE = 'SENSORS_NOTES_CREATION_CLOSE',
  SENSORS_NOTES_CREATION_UPDATE = 'SENSORS_NOTES_CREATION_UPDATE',
  SENSORS_NOTES_CREATION_SUBMIT = 'SENSORS_NOTES_CREATION_SUBMIT',
  SENSORS_NOTES_CREATION_COMPLETE = 'SENSORS_NOTES_CREATION_COMPLETE',
  SENSORS_NOTES_CREATION_ERROR = 'SENSORS_NOTES_CREATION_ERROR',

  SENSORS_NOTES_EDIT_NOTE = 'SENSORS_NOTES_EDIT_NOTE',
  SENSORS_NOTES_EDIT_NOTE_UPDATE = 'SENSORS_NOTES_EDIT_NOTE_UPDATE',
  SENSORS_NOTES_CANCEL_EDIT_NOTE = 'SENSORS_NOTES_CANCEL_EDIT_NOTE',
  SENSORS_NOTES_SUBMIT_EDIT_NOTE = 'SENSORS_NOTES_SUBMIT_EDIT_NOTE',
  SENSORS_NOTES_EDIT_NOTE_COMPLETE = 'SENSORS_NOTES_EDIT_NOTE_COMPLETE',
  SENSORS_NOTES_EDIT_NOTE_ERROR = 'SENSORS_NOTES_EDIT_NOTE_ERROR',

  SENSORS_NOTES_DELETE_NOTE = 'SENSORS_NOTES_DELETE_NOTE',
  SENSORS_NOTES_DELETE_NOTE_CONFIRM = 'SENSORS_NOTES_DELETE_NOTE_COMFIRM',
  SENSORS_NOTES_DELETE_NOTE_DISMISS = 'SENSORS_NOTES_DELETE_NOTE_DISMISS',
  SENSORS_NOTES_DELETE_NOTE_COMPLETE = 'SENSORS_NOTES_DELETE_NOTE_COMPLETE',
  SENSORS_NOTES_DELETE_NOTE_ERROR = 'SENSORS_NOTES_DELETE_NOTE_ERROR',
  SENSORS_NOTES_CLICK_NOTE_ACTION = 'SENSORS_NOTES_CLICK_NOTE_ACTION',
  SENSORS_NOTES_RELOAD = 'SENSORS_NOTES_RELOAD',
  SENSORS_STATE_TRANSITION = 'SENSORS_STATE_TRANSITION',

  SENSORS_CAPTURES_DELETE = 'SENSORS_CAPTURES_DELETE',
  SENSORS_CAPTURES_DELETE_SUCCESS = 'SENSORS_CAPTURES_DELETE_SUCCESS',
  SENSORS_REANNOTATE_DATA_CAPTURE_CHANGE_REASON = 'SENSORS_REANNOTATE_DATA_CAPTURE_CHANGE_REASON',
  SENSORS_CAPTURES_REANNOTATION = 'SENSORS_CAPTURES_REANNOTATION',
}

export type SensorsAction =
  | {
      type: SensorsActionTypes.SENSORS_MOUNT_DETAIL;
      serialNumber: Sensor['serial_number'];
      initialSelectedTab: SensorTabs | null;
    }
  | {
      type: SensorsActionTypes.SENSORS_RELOAD_DETAIL;
      serialNumber: Sensor['serial_number'];
    }
  | {
      type: SensorsActionTypes.SENSORS_REANNOTATE_DATA_CAPTURE_CHANGE_REASON;
      value: ReannotateDataReason;
    }
  | { type: SensorsActionTypes.SENSORS_UNMOUNT_DETAIL }
  | {
      type: SensorsActionTypes.SENSORS_LOAD_START;
    }
  | {
      type: SensorsActionTypes.SENSORS_LOAD_ERROR;
      error: any;
    }
  | {
      type: SensorsActionTypes.SENSORS_LOAD_COMPLETE;
      sensor: Sensor;
    }
  | {
      type: SensorsActionTypes.SENSORS_SWITCH_TAB;
      tab: SensorTabs;
    }
  | { type: SensorsActionTypes.SENSORS_CAPTURES_LOAD_START }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_LOAD_ERROR;
      error: any;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_LOAD_COMPLETE;
      captures: Array<Capture>;
      sensorSerialNumber: number;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_LOAD_COMPLETE;
      captures: Array<Capture>;
      sensorSerialNumber: number;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_SET_FILTERS;
      filters: Array<Filter>;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_SET_SORT;
      direction: SortDirection;
      column: SortColumn;
      captures: Array<Capture>;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_SET_SORT;
      direction: SortDirection;
      column: SortColumn;
      captures: Array<Capture>;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_SET_SEARCH;
      search: string;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_CHANGE_CHECKS;
      ids: Array<Capture['id']>;
      checked: boolean;
    }
  | { type: SensorsActionTypes.SENSORS_NOTES_LOAD_START }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_LOAD_ERROR;
      error: any;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_LOAD_COMPLETE;
      notes: Array<Note>;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CREATION_COMPLETE;
      note: Note;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CREATION_ERROR;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE_COMPLETE;
      noteId: Note['id'];
      noteData: Note['data'];
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE_ERROR;
      noteId: Note['id'];
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CREATION_OPEN;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CREATION_CLOSE;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CREATION_UPDATE;
      body: string;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CREATION_SUBMIT;
      body: string;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE;
      noteId: Note['id'];
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE_UPDATE;
      body: string;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CANCEL_EDIT_NOTE;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_SUBMIT_EDIT_NOTE;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE;
      noteId: Note['id'];
    }
  | { type: SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_DISMISS }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_CONFIRM;
      noteId: Note['id'];
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_COMPLETE;
      noteId: Note['id'];
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_ERROR;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_CLICK_NOTE_ACTION;
      noteId: Note['id'];
      noteAction: NoteAction;
    }
  | {
      type: SensorsActionTypes.SENSORS_NOTES_RELOAD;
    }
  | {
      type: SensorsActionTypes.SENSORS_STATE_TRANSITION;
      state: Sensor['current_state'];
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_DELETE;
      captureIds: Array<Capture['id']>;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_DELETE_SUCCESS;
      captureIds: Array<Capture['id']>;
    }
  | {
      type: SensorsActionTypes.SENSORS_CAPTURES_REANNOTATION;
      captureIds: Array<Capture['id']>;
    };
